package one.healthium.onboarding.ui

import androidx.compose.runtime.Stable
import co.touchlab.kermit.Logger
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.update
import one.healthium.base.preferences.AppPreferences
import one.healthium.domain.user.UserInteractor
import one.healthium.domain.user.models.Gender
import one.healthium.onboarding.domain.models.SelectableOption
import one.healthium.onboarding.ui.models.HeightUnit
import one.healthium.onboarding.ui.models.WeightUnit
import one.healthium.onboarding.ui.models.correspondingHeightUnit
import one.healthium.onboarding.ui.models.correspondingWeightUnit
import one.healthium.ui.common.vm.BaseViewModel

internal class OnboardingViewModel(
    private val userInteractor: UserInteractor,
    private val userMapper: UserUiMapper,
    private val appPreferences: AppPreferences,
) : BaseViewModel(), UserDataContract {

    private val _state = MutableStateFlow(OnboardingUiState())
    val state: StateFlow<OnboardingUiState> = _state

    init {
        launch {
            try {
                val userData = userInteractor.getUser().profile
                _state.update { state ->
                    state.copy(userState = userMapper.toUiModel(userData, appPreferences.isMetric))
                }
            } catch (e: Exception) {
                Logger.e("get user error", e)
            }
        }
        launch {
            appPreferences.isMetricFlow.collect { isMetric ->
                updateUser {
                    copy(
                        isMetric = isMetric,
                    )
                }
            }
        }
    }

    override fun onNameChanged(value: String) {
        updateUser { copy(name = value) }
    }

    override fun onAgeChanged(value: Int?) {
        updateUser { copy(age = value) }
    }

    override fun onWeightChanged(value: Float?) {
        updateUser {
            copy(weight = value)
        }
    }

    override fun onWeightUnitChanged(weightUnit: WeightUnit) {
        appPreferences.isMetric = weightUnit == WeightUnit.Kg
        updateUser {
            copy(
                selectedWeightUnit = weightUnit,
                selectedTargetWeightUnit = weightUnit,
                selectedHeightUnit = weightUnit.correspondingHeightUnit(),
            )
        }
    }

    override fun onTargetWeightChanged(value: Float?) {
        updateUser {
            copy(targetWeight = value)
        }
    }

    override fun onTargetWeightUnitChanged(weightUnit: WeightUnit) {
        appPreferences.isMetric = weightUnit == WeightUnit.Kg
        updateUser {
            copy(
                selectedWeightUnit = weightUnit,
                selectedTargetWeightUnit = weightUnit,
                selectedHeightUnit = weightUnit.correspondingHeightUnit(),
            )
        }
    }

    override fun onHeightChanged(value: Float?) {
        updateUser {
            copy(height = value)
        }
    }

    override fun onHeightUnitChanged(heightUnit: HeightUnit) {
        appPreferences.isMetric = heightUnit == HeightUnit.Cm
        updateUser {
            copy(
                selectedHeightUnit = heightUnit,
                selectedWeightUnit = heightUnit.correspondingWeightUnit(),
                selectedTargetWeightUnit = heightUnit.correspondingWeightUnit(),
            )
        }
    }

    override fun onGenderChanged(gender: Gender) {
        updateUser {
            copy(gender = gender)
        }
    }

    override fun onRelateToStatementChanged(key: String, value: Int) {
        updateUser {
            copy(statements = statements.plus(key to value))
        }
    }

    override fun onAdditionalInfoChanged(key: String, value: List<SelectableOption>) {
        updateUser {
            copy(additionalInfo = additionalInfo.plus(key to value))
        }
    }

    override fun onAdditionalTextChanged(key: String, value: String) {
        updateUser {
            copy(additionalText = additionalText.plus(key to value))
        }
    }

    private fun updateUser(bloc: UserProfileUiModel.() -> UserProfileUiModel) {
        _state.update { state ->
            state.copy(
                userState = bloc(state.userState)
            )
        }
        launch {
            try {
                userInteractor.updateUserProfile(userMapper.toModel(state.value.userState))
            } catch (e: Exception) {
                Logger.e("update user error", e)
            }
        }
    }
}

@Stable
internal data class OnboardingUiState(
    val loading: Boolean = false,
    val userState: UserProfileUiModel = UserProfileUiModel()
) {
    companion object {
        val preview = OnboardingUiState()
    }
}
